import React, { useState, useEffect } from 'react';
import { Button, Icon, Alert, Input } from '@polichat/flamboyant';
import poliConfig from 'src/utils/poliConfig';
import {
  ConfigContainer,
  ConfigCard,
  ConfigHeader,
  ConfigContent,
  ConfigSection,
  ConfigField,
  ConfigLabel,
  ConfigInput,
  ConfigToggle,
  ConfigInfo,
  ActionButtons,
  StatusIndicator,
  SyncStatus
} from './ChannelConfiguration.styles';

const ChannelConfiguration = ({ onComplete, onBack, channelData, customerId }) => {
  const [formData, setFormData] = useState({
    channelName: '',
    displayOnWebchat: false,
    isActive: true
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [syncStatus, setSyncStatus] = useState('synchronizing'); // synchronizing, synchronized

  useEffect(() => {
    // Simular sincronização de histórico
    const syncTimer = setTimeout(() => {
      setSyncStatus('synchronized');
    }, 3000);

    return () => clearTimeout(syncTimer);
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Use the new CoexistenceController endpoint
      const laravelUrl = poliConfig.poliAppLaravelUrl || process.env.REACT_APP_LARAVEL_URL || 'http://localhost:8080';
      const response = await fetch(`${laravelUrl}/channels/whatsapp-coexistence/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
        },
        credentials: 'include',
        body: JSON.stringify({
          customer_id: customerId,
          channel_name: formData.channelName,
          phone_number: channelData.phoneNumber,
          phone_number_id: channelData.phoneNumberId,
          waba_id: channelData.wabaId || channelData.businessId,
          access_token: channelData.accessToken,
          display_on_webchat: formData.displayOnWebchat,
          is_active: formData.isActive,
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erro ao criar canal');
      }

      const data = await response.json();
      
      if (data.success) {
        onComplete();
      } else {
        throw new Error(data.error || 'Erro desconhecido ao criar canal');
      }
      
    } catch (err) {
      console.error('Erro ao salvar canal:', err);
      setError(err.message || 'Erro ao salvar configurações. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const renderSyncStatus = () => {
    if (syncStatus === 'synchronizing') {
      return (
        <SyncStatus>
          <Icon icon="poli-icon pi-loading-fill" size={20} />
          <span>Sincronizando canal: conexão sendo finalizada, mensagens sendo salvas</span>
        </SyncStatus>
      );
    }

    return (
      <SyncStatus>
        <Icon icon="poli-icon pi-check-fill" size={20} color="#00C48C" />
        <span>Histórico sincronizado: canal conectado e histórico de mensagens salvo, nada pendente</span>
      </SyncStatus>
    );
  };

  return (
    <ConfigContainer>
      <ConfigCard>
        <ConfigHeader>
          <Icon icon="poli-icon pi-setting-fill" size={40} color="#00C48C" />
          <h3>Configurar Canal</h3>
        </ConfigHeader>
        
        <ConfigContent>
          {renderSyncStatus()}

          <ConfigSection>
            <h4>Informações do Canal</h4>
            
            <ConfigField>
              <ConfigLabel>Nome do canal</ConfigLabel>
              <ConfigInput>
                <Input
                  type="text"
                  value={formData.channelName}
                  onChange={(e) => handleInputChange('channelName', e.target.value)}
                  placeholder="Ex: WhatsApp Principal"
                />
              </ConfigInput>
            </ConfigField>

            <ConfigField>
              <ConfigLabel>Telefone</ConfigLabel>
              <ConfigInput>
                {channelData.phoneNumber ? (
                  <Input
                    type="text"
                    value={channelData.phoneNumber}
                    disabled
                    style={{ backgroundColor: '#f8f9fa', color: '#666' }}
                  />
                ) : (
                  <div style={{ 
                    padding: '10px', 
                    backgroundColor: '#FFF9E6', 
                    border: '1px solid #FFA500',
                    borderRadius: '4px',
                    color: '#CC8800',
                    fontSize: '0.9em'
                  }}>
                    ⚠️ Número de telefone será registrado automaticamente durante a sincronização
                  </div>
                )}
                <ConfigInfo>
                  Este campo não pode ser editado após a conexão
                </ConfigInfo>
              </ConfigInput>
            </ConfigField>
          </ConfigSection>

          <ConfigSection>
            <h4>Dados do Provedor</h4>
            
            <ConfigField>
              <ConfigLabel>ID do Provedor</ConfigLabel>
              <ConfigInput>
                <Input
                  type="text"
                  value={channelData.businessId || ''}
                  disabled
                  style={{ backgroundColor: '#f8f9fa', color: '#666' }}
                />
              </ConfigInput>
            </ConfigField>

            <ConfigField>
              <ConfigLabel>Status da Conexão</ConfigLabel>
              <StatusIndicator>
                <Icon icon="poli-icon pi-check-fill" size={16} color="#00C48C" />
                <span>Conectado ao provedor</span>
              </StatusIndicator>
            </ConfigField>
          </ConfigSection>

          <ConfigSection>
            <h4>Permissões do Canal</h4>
            
            <ConfigToggle>
              <Input
                type="checkbox"
                checked={formData.displayOnWebchat}
                onChange={(e) => handleInputChange('displayOnWebchat', e.target.checked)}
              />
              <span>Exibir canal no Widget do webchat</span>
            </ConfigToggle>

            <ConfigToggle>
              <Input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => handleInputChange('isActive', e.target.checked)}
              />
              <span>Status ativo</span>
            </ConfigToggle>
          </ConfigSection>

          <ConfigSection>
            <h4>Status da Conexão WhatsApp</h4>
            <StatusIndicator>
              <Icon icon="poli-icon pi-check-fill" size={16} color="#00C48C" />
              <span>Conectado ao WhatsApp</span>
            </StatusIndicator>
          </ConfigSection>

          {error && (
            <Alert color="danger">
              <Icon icon="poli-icon pi-sad-line" size={20} />
              <span>{error}</span>
            </Alert>
          )}

          <ActionButtons>
            <Button
              color="secondary"
              onClick={onBack}
              disabled={isLoading}
            >
              <Icon icon="poli-icon pi-arrow-left-line" size={16} />
              Voltar
            </Button>
            
            <Button
              color="primary"
              onClick={handleSave}
              disabled={isLoading || !formData.channelName.trim()}
            >
              {isLoading ? (
                <>
                  <Icon icon="poli-icon pi-loading-fill" size={16} />
                  Salvando...
                </>
              ) : (
                <>
                  <Icon icon="poli-icon pi-check-fill" size={16} />
                  Salvar Canal
                </>
              )}
            </Button>
          </ActionButtons>
        </ConfigContent>
      </ConfigCard>
    </ConfigContainer>
  );
};

export default ChannelConfiguration;
